import type {
  JevAnswer,
  JevQuestion,
  VoiceScopeCatalogLineItem,
  VoiceScopeCatalogOption,
  VoiceScopeCatalogTask,
  VoiceScopeSnapshotTask,
  VoiceScopeSnapshotValue,
} from './voiceScopeTypes';

export const SCOPE_PROJECTION_NONE_KEY = 'none';
export const MAXIMUM_CHOICE_CATEGORIES = 254;
const LISTED_PRODUCTS = 4;

const IGNORED_WORDS = new Set(['the', 'and', 'with', 'for', 'this', 'that', 'need', 'replace', 'install', 'new', 'one', 'just']);

const words = (text: string) => new Set(text.toLowerCase().split(/[^a-z0-9]+/).filter(word => word.length >= 3 && !IGNORED_WORDS.has(word)));

const formatNumber = (value: number) => Number.isInteger(value) ? String(value) : value.toFixed(2);

export type CatalogEntry = {
  task: VoiceScopeCatalogTask;
  option: VoiceScopeCatalogOption;
  lineItem?: VoiceScopeCatalogLineItem;
  products: VoiceScopeCatalogLineItem[];
  label: string;
  optionKey: string;
  optionLabel: string;
};

const buildCatalogEntry = (
  task: VoiceScopeCatalogTask,
  option: VoiceScopeCatalogOption,
  lineItem: VoiceScopeCatalogLineItem | undefined,
  products: VoiceScopeCatalogLineItem[],
): CatalogEntry => {
  let label: string;
  if (products.length > 0) {
    const listed = products.slice(0, LISTED_PRODUCTS).map(product => product.name).join('; ');
    const more = products.length > LISTED_PRODUCTS ? `; and ${products.length - LISTED_PRODUCTS} more` : '';
    label = `${task.name}: ${option.name} (one of: ${listed}${more})`;
  } else {
    const base = lineItem ? `${task.name}: ${option.name}: ${lineItem.name}` : `${task.name}: ${option.name}`;
    const description = lineItem?.description ?? option.description ?? task.description;
    label = description ? `${base}. ${description.slice(0, 240)}` : base;
  }

  return {
    task,
    option,
    lineItem,
    products,
    label,
    optionKey: `${task.templateId}/${option.templateId}`,
    optionLabel: `${task.name}: ${option.name}`,
  };
};

/** Picks the product line whose title best matches what was said. */
export const pickCatalogProduct = (entry: CatalogEntry, utterance: string): VoiceScopeCatalogLineItem | undefined => {
  if (entry.products.length <= 1) return entry.products[0] ?? entry.lineItem;
  const spoken = words(utterance);
  const scored = entry.products.map(product => ({
    product,
    score: [...words(product.name)].filter(word => spoken.has(word)).length,
  }));
  const best = Math.max(...scored.map(item => item.score), 0);
  const winners = scored.filter(item => item.score === best);
  return best > 0 && winners.length === 1 ? winners[0].product : entry.products[0];
};

const addableEntries = (catalog: VoiceScopeCatalogTask[], tasks: VoiceScopeSnapshotTask[]): CatalogEntry[] => {
  const optionsByTaskTemplate = new Map(tasks.map(task => [task.templateId, task.options]));
  return catalog.flatMap(task =>
    task.options.flatMap(option => {
      const present = optionsByTaskTemplate.get(task.templateId)?.find(existing => existing.templateId === option.templateId);
      if (option.lineItems.length <= 1) {
        return present ? [] : [buildCatalogEntry(task, option, option.lineItems[0], [])];
      }

      const presentLineItems = new Set(present?.lineItems.map(lineItem => lineItem.templateId) ?? []);
      const addable = option.lineItems.filter(lineItem => !presentLineItems.has(lineItem.templateId));
      const labor = addable.filter(lineItem => lineItem.isProduct !== true)
        .map(lineItem => buildCatalogEntry(task, option, lineItem, []));
      const products = addable.filter(lineItem => lineItem.isProduct === true);
      if (products.length === 0) return labor;
      return [...labor, buildCatalogEntry(task, option, products[0], products)];
    })
  );
};

const renderState = (
  groupName: string,
  tasks: VoiceScopeSnapshotTask[],
  transcript: string[],
  recentEdits: string[],
  utterance: string,
): string => {
  const lines = [`ROOM: ${groupName}`, '', "SCOPED TASKS (work already in this room's scope):"];
  const scoped = tasks.filter(task => task.options.length > 0);
  if (scoped.length === 0) lines.push('(none yet)');
  for (const task of scoped) {
    const options = task.options.map(option =>
      option.lineItems.length > 1
        ? `${option.name} (${option.lineItems.map(lineItem => lineItem.name).join(', ')})`
        : option.name
    );
    lines.push(`- ${task.name}: ${options.join('; ')}`);
  }

  lines.push('', 'MEASUREMENTS (all tasks in this room, scoped or not):');
  const measured = tasks.filter(task => task.values.length > 0);
  if (measured.length === 0) lines.push('(none)');
  for (const task of measured) {
    const values = task.values.map(value =>
      `${value.description ?? value.name} = ${value.value === undefined ? 'unset' : formatNumber(value.value)} ${value.type}`
    );
    lines.push(`- ${task.name}: ${values.join('; ')}`);
  }

  lines.push('', 'RECENT SPEECH (oldest first):');
  lines.push(...(transcript.length === 0 ? ['(none)'] : transcript.map(line => `- "${line}"`)));

  lines.push('', 'RECENT EDITS (oldest first):');
  lines.push(...(recentEdits.length === 0 ? ['(none)'] : recentEdits.map(edit => `- ${edit}`)));

  lines.push('', `LATEST UTTERANCE: "${utterance}"`);
  return lines.join('\n');
};

export type ScopeProjection = {
  state: string;
  questions: Record<string, JevQuestion>;
  catalogByKey: Record<string, CatalogEntry>;
  taskByKey: Record<string, VoiceScopeSnapshotTask>;
  valueByKey: Record<string, { task: VoiceScopeSnapshotTask; value: VoiceScopeSnapshotValue }>;
};

export type BuildScopeProjectionOptions = {
  groupName: string;
  tasks: VoiceScopeSnapshotTask[];
  catalog: VoiceScopeCatalogTask[];
  transcript: string[];
  recentEdits?: string[];
  utterance: string;
};

/** Builds the KaiAgent state and question set for one utterance (add/update v1). */
export const buildScopeProjection = ({
  groupName,
  tasks,
  catalog,
  transcript,
  recentEdits = [],
  utterance,
}: BuildScopeProjectionOptions): ScopeProjection => {
  const catalogByKey: Record<string, CatalogEntry> = {};
  const catalogCriteria: Record<string, string> = { [SCOPE_PROJECTION_NONE_KEY]: 'Nothing in the catalog matches what was said' };
  for (const entry of addableEntries(catalog, tasks).slice(0, MAXIMUM_CHOICE_CATEGORIES)) {
    const key = `c${Object.keys(catalogByKey).length + 1}`;
    catalogByKey[key] = entry;
    catalogCriteria[key] = entry.label;
  }

  const taskByKey: Record<string, VoiceScopeSnapshotTask> = {};
  const taskCriteria: Record<string, string> = { [SCOPE_PROJECTION_NONE_KEY]: 'No scoped task is being referred to' };
  for (const task of tasks.filter(task => task.options.length > 0).slice(0, MAXIMUM_CHOICE_CATEGORIES)) {
    const key = `t${Object.keys(taskByKey).length + 1}`;
    taskByKey[key] = task;
    taskCriteria[key] = `${task.name}: ${task.options.map(option => option.name).join(', ')}`;
  }

  const valueByKey: Record<string, { task: VoiceScopeSnapshotTask; value: VoiceScopeSnapshotValue }> = {};
  const valueCriteria: Record<string, string> = { [SCOPE_PROJECTION_NONE_KEY]: 'No measurement or quantity is being changed' };
  for (const task of tasks) {
    for (const value of task.values) {
      if (Object.keys(valueByKey).length >= MAXIMUM_CHOICE_CATEGORIES) break;
      const key = `v${Object.keys(valueByKey).length + 1}`;
      valueByKey[key] = { task, value };
      valueCriteria[key] = `${task.name}: ${value.description ?? value.name} (${value.type})`;
    }
  }

  const questions: Record<string, JevQuestion> = {
    isWork: {
      type: 'noul',
      instructions:
        'Is the LATEST UTTERANCE about the renovation of this room: work to do, how much of it, or work to take out? Ambient conversation, questions, and unrelated talk are not.',
    },
    operation: {
      type: 'choice',
      instructions: "What change to the room's scope does the LATEST UTTERANCE ask for?",
      criteria: {
        add: 'Add work: put a task into scope by choosing what to do for it',
        update: 'Change a quantity, measurement, price, or labor price of work already in scope',
        [SCOPE_PROJECTION_NONE_KEY]: 'No change to the scope',
      },
    },
  };

  if (Object.keys(catalogByKey).length > 0) {
    questions.catalogOption = {
      type: 'choice',
      instructions:
        'If the LATEST UTTERANCE adds work, which catalog entry (task, what to do for it, and which line of work) is it? Pick none if it adds no work or nothing matches.',
      criteria: catalogCriteria,
    };
  }

  if (Object.keys(taskByKey).length > 0) {
    questions.targetTask = {
      type: 'choice',
      instructions: 'Which SCOPED task does the LATEST UTTERANCE refer to? Pick none if it refers to no scoped task.',
      criteria: taskCriteria,
    };
  }

  if (Object.keys(valueByKey).length > 0) {
    questions.targetValue = {
      type: 'choice',
      instructions: 'If the LATEST UTTERANCE changes a quantity, measurement, price, or labor price, which one? Pick none if it changes no number.',
      criteria: valueCriteria,
    };
  }

  return {
    state: renderState(groupName, tasks, transcript, recentEdits, utterance),
    questions,
    catalogByKey,
    taskByKey,
    valueByKey,
  };
};

export type BuildScopedUpdateProjectionOptions = Omit<BuildScopeProjectionOptions, 'catalog'>;

/**
 * JEV projection for the concurrent scoped-update channel: only work already in scope,
 * no catalog adds. Operation is update-or-none only.
 */
export const buildScopedUpdateProjection = ({
  groupName,
  tasks,
  transcript,
  recentEdits = [],
  utterance,
}: BuildScopedUpdateProjectionOptions): ScopeProjection | undefined => {
  const scopedTasks = tasks.filter(task => task.options.length > 0);
  if (scopedTasks.length === 0) return undefined;

  const projection = buildScopeProjection({
    groupName,
    tasks: scopedTasks,
    catalog: [],
    transcript,
    recentEdits,
    utterance,
  });

  return {
    ...projection,
    questions: {
      ...projection.questions,
      operation: {
        type: 'choice',
        instructions: "Does the LATEST UTTERANCE change a quantity, measurement, price, or labor price on work already in this room's scope?",
        criteria: {
          update: 'Change a number on scoped work',
          [SCOPE_PROJECTION_NONE_KEY]: 'No measurement on scoped work is being changed',
        },
      },
    },
  };
};

const jevProbabilityOf = (answer: JevAnswer | undefined, category: string): number =>
  answer?.probabilities?.[category] ?? (answer?.choice === category ? answer.confidence ?? 0 : 0);

/** Human-readable summary of the catalog, task, and value picks KaiAgent weighed. */
export const describeJevPicks = (
  answers: Record<string, JevAnswer>,
  projection: ScopeProjection,
): string => {
  const catalogCandidates = Object.entries(answers.catalogOption?.probabilities ?? {})
    .filter(([key, probability]) => key !== SCOPE_PROJECTION_NONE_KEY && probability >= 0.05)
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .flatMap(([key, probability]) => {
      const entry = projection.catalogByKey[key];
      return entry ? [`${Math.round(probability * 100)}% "${entry.label.slice(0, 90)}"`] : [];
    });

  const picks: [string, string | undefined][] = [
    ['catalog', catalogCandidates.length > 0 ? catalogCandidates.join(' / ') : undefined],
    ['task', answers.targetTask?.choice ? projection.taskByKey[answers.targetTask.choice]?.name : undefined],
    [
      'value',
      answers.targetValue?.choice
        ? projection.valueByKey[answers.targetValue.choice]
          && `${projection.valueByKey[answers.targetValue.choice]!.task.name}: ${
            projection.valueByKey[answers.targetValue.choice]!.value.description ?? projection.valueByKey[answers.targetValue.choice]!.value.name
          }`
        : undefined,
    ],
  ];

  const described = picks.flatMap(([name, label]) => label ? [`${name}→${label}`] : []);
  return described.length === 0 ? 'none' : described.join(', ');
};
